import {
  Box,
  SxProps,
  Tooltip,
  BoxProps,
  IconButton,
  Typography,
  IconButtonProps,
} from "@mui/material";
import { Icon } from "../Icon";
import { forwardRef, ReactElement } from "react";
import { Checkbox } from "../Checkbox";
import { NEW_FOLDER_ICON } from "../FolderTree/newFolderIcon";
import { COLOR_CONSTANTS, TYPOGRAPHY_SCALE } from "../../constants/styles";

/** title 지정 시에만 Tooltip 으로 감싼다. disabled 버튼도 툴팁이 뜨도록 span 래퍼를 둔다 (MUI 관례). */
function ButtonTooltip({
  title,
  children,
}: {
  title?: string;
  children: ReactElement;
}) {
  if (!title) return children;
  return (
    <Tooltip title={title}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

/**
 * 헤더 아이콘 버튼 한 칸 — FileHeader 의 다섯 버튼(새 폴더·first·edit·second·third)이 모두 이걸로 그린다.
 * `color` 는 테두리와 아이콘에 함께 쓰이고, `buttonProps.disabled` 이면 둘 다 `action.disabled` 로 바뀐다.
 * `buttonProps` 는 size·sx·onClick 뒤에 펼치므로 같은 키를 넘기면 소비처 값이 이긴다.
 */
function HeaderIconButton({
  tooltip,
  iconName,
  color,
  onClick,
  buttonProps,
}: {
  tooltip?: string;
  iconName: string;
  color: string;
  onClick?: () => void;
  buttonProps?: IconButtonProps;
}) {
  return (
    <ButtonTooltip title={tooltip}>
      <IconButton
        size="small"
        sx={{
          ...iconButton,
          border: `1px solid ${color}`,
          ...(buttonProps?.disabled && disabledHeaderButton),
        }}
        onClick={onClick}
        {...buttonProps}
      >
        <Icon
          name={iconName}
          sx={icon}
          size={HEADER_ICON_SIZE}
          color={
            buttonProps?.disabled ? COLOR_CONSTANTS.action.disabled : color
          }
        />
      </IconButton>
    </ButtonTooltip>
  );
}

/**
 * 파일 섹션 헤더 props — 제목·전체선택 체크박스 + 최대 5개의 아이콘 버튼(보통 [새 폴더]/다운로드/[수정]/삭제/업로드).
 * 수정 버튼(edit)은 opt-in — `showEditButton` 지정 시에만 first(다운로드)와 second(삭제) 사이에 렌더된다.
 * 새 폴더 버튼도 opt-in — `showNewFolderButton` 지정 시에만 맨 앞에 렌더된다(파일 조작이 아니라 정리 조작이라
 * 파일 버튼 묶음과 떨어뜨린다).
 */
export interface FileHeaderProps extends BoxProps {
  title: string;
  checked?: boolean;
  showFirstButton?: boolean;
  /** 수정 버튼 노출 여부. 기본 false — 기존 소비처는 3버튼 그대로. */
  showEditButton?: boolean;
  /** 새 폴더 버튼 노출 여부. 기본 false — 폴더 쓰기 권한자에게만 연다. */
  showNewFolderButton?: boolean;
  onNewFolderButtonClick?: () => void;
  /** 아이콘 전용 버튼이므로 박스 제목을 넣은 `aria-label` 과, 쓰기가 잠긴 동안의 `disabled` 를 여기로 전달한다. */
  newFolderButtonProps?: IconButtonProps;
  newFolderButtonTooltip?: string;
  onCheck?: () => void;
  onFirstButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onSecondButtonClick?: () => void;
  onThirdButtonClick?: () => void;
  firstButtonIcon?: string;
  editButtonIcon?: string;
  secondButtonIcon?: string;
  thirdButtonIcon?: string;
  firstButtonColor?: string;
  editButtonColor?: string;
  secondButtonColor?: string;
  thirdButtonColor?: string;
  firstButtonProps?: IconButtonProps;
  /** 아이콘 전용 버튼이므로 `aria-label`, 토글 용도라면 `aria-pressed` 를 여기로 전달한다. */
  editButtonProps?: IconButtonProps;
  secondButtonProps?: IconButtonProps;
  thirdButtonProps?: IconButtonProps;
  /** 지정 시 해당 버튼에 호버 툴팁 표시 (disabled 상태 포함). */
  firstButtonTooltip?: string;
  editButtonTooltip?: string;
  secondButtonTooltip?: string;
  thirdButtonTooltip?: string;

  sx?: SxProps;
}

export const FileHeader = forwardRef<HTMLDivElement, FileHeaderProps>(
  (
    {
      title = "File Name",
      checked,
      onCheck,
      onFirstButtonClick,
      onEditButtonClick,
      onSecondButtonClick,
      onThirdButtonClick,
      sx,
      showFirstButton = true,
      showEditButton = false,
      showNewFolderButton = false,
      onNewFolderButtonClick,
      newFolderButtonProps,
      newFolderButtonTooltip,
      firstButtonIcon = "etc/FileDownload",
      editButtonIcon = "etc/pencil-line",
      secondButtonIcon = "20/etc/trash",
      thirdButtonIcon = "etc/UploadFile",
      firstButtonColor = COLOR_CONSTANTS.primary.main,
      editButtonColor = COLOR_CONSTANTS.primary.main,
      secondButtonColor = COLOR_CONSTANTS.error.main,
      thirdButtonColor = COLOR_CONSTANTS.primary.main,
      firstButtonProps,
      editButtonProps,
      secondButtonProps,
      thirdButtonProps,
      firstButtonTooltip,
      editButtonTooltip,
      secondButtonTooltip,
      thirdButtonTooltip,
      ...props
    },
    ref
  ) => {
    return (
      <Box ref={ref} sx={{ ...container, ...sx }} {...props}>
        <Checkbox
          size="small"
          onChange={onCheck}
          checked={checked}
          /*
            보이는 제목이 곧 전체선택 체크박스의 접근명이다 — 옆의 제목은 형제 요소일 뿐 label 이
            아니라서, 이름이 없으면 낭독에 「checkbox, not checked」만 남아 이 선택이 무엇에
            걸리는지 사라진다.
          */
          slotProps={{ input: { "aria-label": title } }}
        />
        <Typography sx={{ ...TYPOGRAPHY_SCALE.buttonMedium, ...text }}>
          {title}
        </Typography>
        <Box sx={buttonBox}>
          {showNewFolderButton && (
            <HeaderIconButton
              tooltip={newFolderButtonTooltip}
              iconName={NEW_FOLDER_ICON}
              color={COLOR_CONSTANTS.primary.main}
              onClick={onNewFolderButtonClick}
              buttonProps={newFolderButtonProps}
            />

          )}
          {showFirstButton && (
            <HeaderIconButton
              tooltip={firstButtonTooltip}
              iconName={firstButtonIcon}
              color={firstButtonColor}
              onClick={onFirstButtonClick}
              buttonProps={firstButtonProps}
            />
          )}
          {showEditButton && (
            <HeaderIconButton
              tooltip={editButtonTooltip}
              iconName={editButtonIcon}
              color={editButtonColor}
              onClick={onEditButtonClick}
              buttonProps={editButtonProps}
            />
          )}
          <HeaderIconButton
            tooltip={secondButtonTooltip}
            iconName={secondButtonIcon}
            color={secondButtonColor}
            onClick={onSecondButtonClick}
            buttonProps={secondButtonProps}
          />
          <HeaderIconButton
            tooltip={thirdButtonTooltip}
            iconName={thirdButtonIcon}
            color={thirdButtonColor}
            onClick={onThirdButtonClick}
            buttonProps={thirdButtonProps}
          />
        </Box>
      </Box>
    );
  }
);

FileHeader.displayName = "FileHeader";

const container = {
  width: "100%",
  height: "40px",
  padding: "4px 8px 4px 0",
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
} as const;

const text = {
  flex: 1,
  minWidth: 0,
  marginRight: "10px",
  overflow: "hidden",
  whiteSpace: "nowrap",
  "&:hover": {
    overflow: "auto",
    textOverflow: "unset",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
} as const;

const iconButton = {
  border: "1px solid theme.palette.primary.main",
  borderRadius: "4px",
  "&:hover": {
    cursor: "pointer",
  },
} as const;

/**
 * 비활성 헤더 아이콘 버튼의 테두리 색 — 아이콘 색도 같은 `action.disabled` 로 바뀐다(HeaderIconButton).
 * 커서는 두지 않는다: 비활성 버튼에는 MUI `.Mui-disabled`(`pointer-events: none`·`cursor: default`)가
 * 붙어 포인터가 버튼 아래 요소에 닿으므로, 여기 둔 커서는 화면에 나타나지 않는다.
 */
const disabledHeaderButton = {
  borderColor: COLOR_CONSTANTS.action.disabled,
} as const;

const icon = {
  "&:hover": {
    cursor: "pointer",
  },
} as const;

/** 헤더 아이콘 버튼 안 아이콘 크기(px). */
const HEADER_ICON_SIZE = 16;

const buttonBox = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
};
